import React from 'react';
import { X } from 'lucide-react';

interface SuggestLineupsModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const SuggestLineupsModal: React.FC<SuggestLineupsModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="flex-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Suggest Lineups
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 w-9 h-9 flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <div className="mb-6 text-sm text-gray-600 dark:text-gray-400 space-y-4">
            <p className="leading-relaxed">
              Intelligently fill empty lineup slots across all upcoming matches in one click. The algorithm optimizes your roster so every match gets the best available players — and no one gets overloaded.
            </p>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2.5">
                <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                <span><span className="font-medium text-gray-700 dark:text-gray-300">Non-destructive</span> — only fills empty positions, never overwrites existing assignments</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                <span><span className="font-medium text-gray-700 dark:text-gray-300">Availability-aware</span> — prioritizes "Available", then "If Needed", skips "Not Available"</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                <span><span className="font-medium text-gray-700 dark:text-gray-300">Preference-driven</span> — respects lineup type preferences and position rankings</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                <span><span className="font-medium text-gray-700 dark:text-gray-300">Strength-optimized</span> — strongest players go to the toughest matchups first</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                <span><span className="font-medium text-gray-700 dark:text-gray-300">Balanced workload</span> — spreads assignments evenly across matches</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                <span><span className="font-medium text-gray-700 dark:text-gray-300">Rule-compliant</span> — enforces gender and combined NTRP rating requirements</span>
              </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-500">
              Suggestions are applied as <span className="italic">unsaved changes</span> — review, adjust, and save when you're ready.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
            >
              Suggest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestLineupsModal;
